// KPI Overview panel — orchestrates the three-layer "scan → trend →
// act" layout in the KPI tab. Owns local period-range state and derives
// all summary/trend/sparkline data from pure helpers in lib/kpi/summary.
// Mounts the KpiDetailDrawer when `selectedKpiId` points at a KPI in
// the current list; closing the drawer clears the selection.

import { useMemo, useState, lazy, Suspense, type JSX } from "react";
import Button from "../../../../common/Button";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import type {
  Kpi,
  KpiMeasurement,
  KpiReport,
} from "../../../../../types";
import {
  categoriseKpiByRag,
  computeCompliancePeriods,
  computeFailingPeriodStats,
  computeKpiAverage,
  computeKpiCompliance,
  computeOverallCompliance,
  dedupeMeasurementsByPeriod,
  dedupeReportsByPeriod,
  normaliseSparklinePoints,
  TARGET_COMPLIANCE_DEFAULT,
  type PeriodRange,
} from "../../../../../lib/kpi/summary";
import {
  formatPeriodLabel,
  normaliseFrequency,
  periodKeyFromDate,
} from "../../../../../lib/kpi/period";
import KpiCard, { type KpiCardRag } from "./KpiCard";
import KpiSummaryStrip from "./KpiSummaryStrip";
import PeriodRangeToggle from "./PeriodRangeToggle";
import KpiDetailDrawer from "../drawer/KpiDetailDrawer";
import ServiceCreditPanel from "../credits/ServiceCreditPanel";

// Lazy-loaded so recharts ships as an on-demand chunk fetched only when the KPI
// overview panel renders its compliance trend, not in the eager bundle.
const KpiComplianceChart = lazy(() => import("./KpiComplianceChart"));

export interface KpiOverviewPanelProps {
  contractId: string;
  kpis: Kpi[];
  reports: KpiReport[];
  measurements: KpiMeasurement[];
  /** Per-contract compliance target (1–100). Null / undefined falls
   *  back to `TARGET_COMPLIANCE_DEFAULT` (95). Drives the chart's
   *  reference line, the summary-strip Compliance-rate tile's period
   *  hit count, and the chart legend label. */
  complianceTarget?: number | null;
  selectedKpiId?: string | null;
  /** Fallback KPI shown in the drawer when `selectedKpiId` is not in
   *  `kpis` (e.g. archived deep-link). */
  fallbackSelectedKpi?: Kpi | null;
  onSelectKpi: (id: string | null) => void;
  onAddKpi: () => void;
  onEditKpi?: (kpi: Kpi) => void;
  onBulkLogPeriod?: () => void;
}

/** A compliance figure for prose, or a dash when it was withheld. Never 0%:
 *  nothing measured and everything failed are opposite facts. */
function percentOrDash(value: number | null): string {
  return value === null ? "—" : `${value}%`;
}

function currentPeriodLabel(kpis: Kpi[]): string {
  // Use the dominant frequency across the KPI set to pick a label
  // format. Monthly ends up as "APR 2026", quarterly "Q1 2026", etc.
  const now = new Date();
  const freq = normaliseFrequency(
    kpis[0]?.measurementFrequency ?? "monthly",
  );
  const key = periodKeyFromDate(now, freq);
  return formatPeriodLabel(key, freq).toUpperCase();
}

function latestReportByKpi(reports: KpiReport[]): Map<string, KpiReport> {
  // Dedupe by (parentId, period) first — if storage has two reports for
  // the same period we want the latest-logged one to drive the card RAG.
  const byParent = new Map<string, KpiReport[]>();
  for (const r of reports) {
    const arr = byParent.get(r.parentId);
    if (arr) arr.push(r);
    else byParent.set(r.parentId, [r]);
  }
  const out = new Map<string, KpiReport>();
  for (const [parentId, list] of byParent) {
    const deduped = dedupeReportsByPeriod(list);
    let latest = deduped[0];
    for (const r of deduped) {
      if (r.period.localeCompare(latest.period) > 0) latest = r;
    }
    if (latest) out.set(parentId, latest);
  }
  return out;
}

function latestMeasurementByKpi(
  measurements: KpiMeasurement[],
): Map<string, KpiMeasurement> {
  // Compare by reporting period (YYYY-MM, YYYY-Qn, etc.), not the wall-
  // clock log date: when multiple periods are logged in a single session
  // they share a `date`, so date-ordering picks the first-inserted record
  // rather than the latest period. Per-period duplicates are collapsed
  // first via dedupeMeasurementsByPeriod so the hero value reflects the
  // latest amendment within the latest period.
  const byParent = new Map<string, KpiMeasurement[]>();
  for (const m of measurements) {
    const arr = byParent.get(m.parentId);
    if (arr) arr.push(m);
    else byParent.set(m.parentId, [m]);
  }
  const out = new Map<string, KpiMeasurement>();
  for (const [parentId, list] of byParent) {
    const deduped = dedupeMeasurementsByPeriod(list);
    let latest = deduped[0];
    for (const m of deduped) {
      if (m.period.localeCompare(latest.period) > 0) latest = m;
    }
    if (latest) out.set(parentId, latest);
  }
  return out;
}

function measurementsByKpi(
  measurements: KpiMeasurement[],
): Map<string, KpiMeasurement[]> {
  const out = new Map<string, KpiMeasurement[]>();
  for (const m of measurements) {
    const arr = out.get(m.parentId);
    if (arr) arr.push(m);
    else out.set(m.parentId, [m]);
  }
  return out;
}

export default function KpiOverviewPanel({
  contractId,
  kpis,
  reports,
  measurements,
  complianceTarget,
  selectedKpiId,
  fallbackSelectedKpi,
  onSelectKpi,
  onAddKpi,
  onEditKpi,
  onBulkLogPeriod,
}: KpiOverviewPanelProps): JSX.Element {
  const [range, setRange] = useState<PeriodRange>("12m");
  const resolvedComplianceTarget =
    typeof complianceTarget === "number"
      ? complianceTarget
      : TARGET_COMPLIANCE_DEFAULT;

  const selectedKpi = useMemo(() => {
    if (!selectedKpiId) return null;
    const inList = kpis.find((k) => k.id === selectedKpiId) ?? null;
    if (inList) return inList;
    if (fallbackSelectedKpi && fallbackSelectedKpi.id === selectedKpiId) {
      return fallbackSelectedKpi;
    }
    return null;
  }, [kpis, selectedKpiId, fallbackSelectedKpi]);

  const compliance = useMemo(
    () => computeOverallCompliance(kpis, reports, range),
    [kpis, reports, range],
  );

  const latestReports = useMemo(() => latestReportByKpi(reports), [reports]);
  const latestMeasurements = useMemo(
    () => latestMeasurementByKpi(measurements),
    [measurements],
  );
  const measurementsGrouped = useMemo(
    () => measurementsByKpi(measurements),
    [measurements],
  );

  const ragCounts = useMemo(
    () => categoriseKpiByRag(kpis, latestReports),
    [kpis, latestReports],
  );

  // Count of KPIs that have at least one report — the denominator
  // backing the compliance chart's weighted average.
  const reportingKpiCount = useMemo(() => {
    const reported = new Set<string>();
    for (const r of reports) {
      if (kpis.some((k) => k.id === r.parentId)) {
        reported.add(r.parentId);
      }
    }
    return reported.size;
  }, [kpis, reports]);

  const periodHitStats = useMemo(
    () =>
      computeCompliancePeriods(compliance.byPeriod, resolvedComplianceTarget),
    [compliance.byPeriod, resolvedComplianceTarget],
  );

  const failingPeriodStats = useMemo(
    () => computeFailingPeriodStats(kpis, reports, compliance.byPeriod),
    [kpis, reports, compliance.byPeriod],
  );

  // Per-card derivations (sparkline / compliance / average) are pure in
  // (kpis, reports, measurements, range). Memoise them so selecting a card
  // or opening the drawer doesn't recompute every card's series.
  const cardModels = useMemo(
    () =>
      kpis.map((kpi) => {
        const latestReport = latestReports.get(kpi.id);
        const rag: KpiCardRag = latestReport?.ragRating ?? "neutral";
        const kpiMeasurements = measurementsGrouped.get(kpi.id) ?? [];
        return {
          kpi,
          latestReport,
          rag,
          sparklinePoints: normaliseSparklinePoints(
            kpiMeasurements,
            kpi.measurementFrequency,
            range,
            undefined,
            kpi.valueType,
          ).map((p) => p.y),
          compliancePercent: computeKpiCompliance(kpi.id, reports, [kpi], range),
          averageValue: computeKpiAverage(
            kpiMeasurements,
            kpi.measurementFrequency,
            range,
            undefined,
            kpi.valueType,
          ),
        };
      }),
    [kpis, reports, latestReports, measurementsGrouped, range],
  );

  if (kpis.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-800"
        data-testid="kpi-overview-empty"
      >
        <h3 className="text-h3 text-slate-900 dark:text-slate-100">
          No KPIs yet
        </h3>
        <p className="text-body-sm text-slate-600 dark:text-slate-400">
          Add one to start tracking performance.
        </p>
        <Button variant="primary" onClick={onAddKpi}>
          + New KPI
        </Button>
      </div>
    );
  }

  // Delta vs the previous reporting month on the compliance trend. Null when
  // fewer than two months in the range carry a result — avoids "N/A" clutter.
  const byPeriod = compliance.byPeriod;
  const previousPeriodValue =
    byPeriod.length >= 2 ? byPeriod[byPeriod.length - 2].value : null;
  const currentPeriodValue =
    byPeriod.length >= 1 ? byPeriod[byPeriod.length - 1].value : null;
  const complianceDelta =
    previousPeriodValue !== null && currentPeriodValue !== null
      ? currentPeriodValue - previousPeriodValue
      : null;

  return (
    <div className="space-y-4" data-testid="kpi-overview-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-h2 text-slate-900 dark:text-slate-100">
            Performance overview
          </h2>
          <p className="mt-1 text-body-sm text-slate-500 dark:text-slate-400">
            Current period performance against contract SLAs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodRangeToggle value={range} onChange={setRange} />
          {onBulkLogPeriod && (
            /* Primary action on the Overview toggle. Logging happens
               10× more often than adding once the KPI set exists, per
               locked rationale. The shell's `+ New KPI` button demotes
               to secondary in parallel. */
            <Button
              variant="primary"
              size="sm"
              onClick={onBulkLogPeriod}
            >
              + Log period (bulk)
            </Button>
          )}
        </div>
      </div>

      <KpiSummaryStrip
        currentPeriodLabel={currentPeriodLabel(kpis)}
        totalKpis={kpis.length}
        overallPercent={compliance.actual}
        onTrack={ragCounts.onTrack}
        atRisk={ragCounts.atRisk}
        failing={ragCounts.failing}
        periodHits={periodHitStats.hits}
        periodTotal={periodHitStats.periods}
        failingPeriods={failingPeriodStats.failingPeriodCount}
        failingPeriodKpiCount={failingPeriodStats.kpiCount}
        failingInLastQuarter={failingPeriodStats.inLastQuarter}
        range={range}
      />

      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60">
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h3 className="text-h4 text-slate-900 dark:text-slate-100">
              Overall compliance trend
            </h3>
            {/* "Weighted average across N KPIs" now lives on the chart
                itself (next to the amber-partial-credit help icon) —
                having it in both places was a duplicate. The chart is
                the single source of
                truth so exports / screenshots carry both "what" and
                "how" the metric is computed. */}
          </div>
          {currentPeriodValue !== null && (
            <div
              className="flex items-baseline gap-2"
              data-testid="kpi-compliance-header-value"
            >
              <span className="text-h2 leading-none text-slate-900 dark:text-slate-100">
                {currentPeriodValue}%
              </span>
              {complianceDelta !== null && complianceDelta !== 0 && (
                <span
                  className={`text-caption ${
                    complianceDelta > 0
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                  data-testid="kpi-compliance-header-delta"
                >
                  <span aria-hidden="true">
                    {complianceDelta > 0 ? "▲" : "▼"}
                  </span>{" "}
                  {Math.abs(complianceDelta)}pt vs prev month
                </span>
              )}
            </div>
          )}
        </div>
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          }
        >
          <KpiComplianceChart
            series={compliance.byPeriod.map((p) => ({
              period: p.period,
              actual: p.value,
            }))}
            targetPercent={resolvedComplianceTarget}
            kpiCount={reportingKpiCount}
            // Names BOTH figures: the headline beside the chart is the
            // latest month, so a label quoting only the range average would
            // describe a number that is nowhere on screen. A withheld figure
            // is spoken as a dash, matching what is rendered.
            ariaLabel={
              `Overall compliance trend, latest month ${percentOrDash(currentPeriodValue)}, ` +
              `${percentOrDash(compliance.actual)} across the range, ` +
              `against ${resolvedComplianceTarget}% target`
            }
          />
        </Suspense>
      </div>

      {/* Credits are recorded here, beside the results that justify them,
          and render read-only on the Reports tab. */}
      <ServiceCreditPanel
        contractId={contractId}
        kpis={kpis}
        reports={reports}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {cardModels.map(
          ({ kpi, latestReport, rag, sparklinePoints, compliancePercent, averageValue }) => (
            <KpiCard
              key={kpi.id}
              kpi={kpi}
              latestMeasurement={latestMeasurements.get(kpi.id)}
              latestReport={latestReport}
              sparklinePoints={sparklinePoints}
              rag={rag}
              compliancePercent={compliancePercent}
              averageValue={averageValue}
              range={range}
              isSelected={kpi.id === selectedKpiId}
              onClick={() => onSelectKpi(kpi.id)}
            />
          ),
        )}
      </div>

      <KpiDetailDrawer
        isOpen={selectedKpi !== null}
        onClose={() => onSelectKpi(null)}
        kpi={selectedKpi}
        contractId={contractId}
        onEdit={(k) => onEditKpi?.(k)}
        range={range}
      />
    </div>
  );
}
